import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'es-toolkit';
import {
  Decimal,
  ZERO,
  parseDecimal,
  differsBeyondTolerance,
  roundToCurrencyPrecision
} from '@insio/decimal';
import type { CurrencyCode } from '@insio/shared-types';
import type { VatRateOption } from '../../../../components/inputs';
import type { InvoiceComputedVatSummary } from '../../../../graphql/generated/graphql';
import { resolveExchangeRateValue } from '../../invoice-totals/resolve-exchange-rate-value';
import {
  applyRawValues,
  toInternalRow,
  type ExpectedFromLines,
  type InternalVatRow,
  type VatSummaryCurrentData,
  type VatSummaryFormData
} from '../utils';

type RowField = 'netAmount' | 'vatAmount' | 'vatRate';

/** Live recap currency + rate the recompute must convert into. Omitted fields
 * fall back to the host's persisted invoice values. */
export interface RecomputeVatSummaryOverrides {
  vatSummaryCurrencyCode?: string | null;
  exchangeRate?: Decimal | null;
}

/** Resolves the line-derived VAT summary via `invoiceComputedVatSummary`.
 * Returns `null` on failure so the editor silently falls back to local math. */
export type RecomputeVatSummaryFn = (
  overrides?: RecomputeVatSummaryOverrides
) => Promise<InvoiceComputedVatSummary | null>;

interface UseVatRowEditorOptions {
  open: boolean;
  currentData: VatSummaryCurrentData;
  vatRateOptions?: VatRateOption[];
  onSave: (data: VatSummaryFormData) => Promise<void>;
  onClose: () => void;
  errorMessage: string;
  /** Backend recompute of the VAT breakdown from line items. When provided,
   * its per-rate amounts drive the expected-value hints and the "fill from
   * lines" action. */
  recompute?: RecomputeVatSummaryFn;
}

const parseAmount = (raw: string): Decimal => parseDecimal(raw) ?? ZERO;

/** Parse to Decimal, returning null when the input is blank (no implicit zero). */
const parseOptionalAmount = (raw: string): Decimal | null =>
  raw.trim() === '' ? null : (parseDecimal(raw) ?? null);

const sortByRate = (rows: InternalVatRow[]) => [...rows].sort((a, b) => a.vatRate - b.vatRate);

/** Index a line-derived summary by VAT rate, so lookups survive row reordering. */
const toExpectedByRate = (
  computed: InvoiceComputedVatSummary | null
): Map<number, ExpectedFromLines> => {
  const map = new Map<number, ExpectedFromLines>();
  for (const item of computed?.items ?? []) {
    if (item.vatRate == null) continue;
    map.set(item.vatRate.toNumber(), {
      netAmount: item.netAmount ?? ZERO,
      vatAmount: item.vatAmount ?? ZERO
    });
  }
  return map;
};

/** Invariant: a 0 % rate row carries no VAT, so force `vatAmount` to zero
 * regardless of what the persisted data says. Keeps the row's totals and the
 * VAT cell's disabled-display in sync. */
const normaliseZeroRateVat = (row: InternalVatRow): InternalVatRow =>
  row.vatRate === 0 && !row.vatAmount.eq(ZERO) ? { ...row, vatAmount: ZERO } : row;

/** Build the initial rows from the persisted summary only — rates with no
 * persisted amounts are not pre-filled. Missing rates are added on demand via
 * "Add rate" or "Fill from line items". */
const buildInitialRows = (existing: InternalVatRow[]): InternalVatRow[] =>
  sortByRate(existing.map(normaliseZeroRateVat));

/** Pick a starting rate for a new custom row that doesn't collide with any
 * existing rate. Searches 0–100 in order. */
const pickUnusedRate = (rows: InternalVatRow[]): number => {
  const used = new Set(rows.map(r => r.vatRate));
  for (let i = 0; i <= 100; i++) {
    if (!used.has(i)) return i;
  }
  return 0;
};

export function useVatRowEditor({
  open,
  currentData,
  vatRateOptions,
  onSave,
  onClose,
  errorMessage,
  recompute,
  rethrowOnError = false
}: UseVatRowEditorOptions & { rethrowOnError?: boolean }) {
  const hasFixedRates = !!vatRateOptions && vatRateOptions.length > 0;

  const [vatRows, setVatRows] = useState<InternalVatRow[]>([]);
  const [rawValues, setRawValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [removeConfirmIdx, setRemoveConfirmIdx] = useState<number | null>(null);
  const [currencyCode, setCurrencyCode] = useState<string | null>(
    currentData.vatSummaryCurrencyCode ?? null
  );
  // Raw exchange-rate buffer so the input shows exactly what was typed; parsed
  // into `exchangeRate` on blur. Rates are never rounded to currency precision.
  const [exchangeRate, setExchangeRate] = useState<Decimal | null>(
    currentData.vatSummaryExchangeRate?.rate ?? null
  );
  const [rawExchangeRate, setRawExchangeRate] = useState<string | null>(null);
  // In-flight guard for "Fill from line items" — it now awaits a recompute, so
  // the toolbar button must not be re-triggered mid-request.
  const [fillingFromLines, setFillingFromLines] = useState(false);
  // Per-row record of the last amount cell the user typed into. Drives which
  // side of the cross-expectation hint pair is visible: editing net hides the
  // net-side hint and keeps the vat-side hint, and vice versa. Rows the user
  // hasn't touched yet keep both hints visible so load-time mismatches surface.
  const [lastEditedByRow, setLastEditedByRow] = useState<Map<number, 'netAmount' | 'vatAmount'>>(
    new Map()
  );
  // Backend line-derived VAT summary; null until fetched (or when no recompute
  // is wired). Drives the expected-value hints and the "fill from lines" action.
  const [computed, setComputed] = useState<InvoiceComputedVatSummary | null>(null);

  // Track whether the modal was previously open so we only re-initialise rows
  // when the modal transitions from closed → open, NOT on every render.
  const prevOpenRef = useRef(false);

  // Fetch the line-derived summary when the editor opens, and again whenever the
  // recap currency or the committed rate changes — the backend converts the line
  // amounts with those two, so a stale pair yields amounts in the wrong currency.
  // A monotonic id discards a response superseded by a newer request.
  const fetchIdRef = useRef(0);
  // Keyed on the rate's string value: `commitExchangeRate` builds a new Decimal
  // on every blur, so the object identity alone would refetch on a no-op blur.
  const exchangeRateKey = exchangeRate?.toString() ?? '';
  useEffect(() => {
    if (!open || !recompute) return;
    const id = ++fetchIdRef.current;
    void (async () => {
      try {
        const result = await recompute({
          vatSummaryCurrencyCode: currencyCode,
          exchangeRate: parseOptionalAmount(exchangeRateKey)
        });
        if (fetchIdRef.current === id) setComputed(result);
      } catch (e) {
        if (fetchIdRef.current === id) {
          // eslint-disable-next-line no-console
          console.error('Failed to recompute VAT summary from lines:', e);
          setComputed(null);
        }
      }
    })();
  }, [open, recompute, currencyCode, exchangeRateKey]);

  // Per-rate expected net/vat from the line-derived summary. Keyed by rate so
  // the lookup survives row reordering and rate edits.
  const expectedByRate = useMemo(() => toExpectedByRate(computed), [computed]);

  const getExpectedForRate = useCallback(
    (rate: number): ExpectedFromLines | null => expectedByRate.get(rate) ?? null,
    [expectedByRate]
  );

  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;

    if (!open) {
      setRemoveConfirmIdx(null);
      return;
    }

    if (wasOpen) return;

    const existing = (currentData.vatSummary ?? []).map((item, persistedIndex) => ({
      ...toInternalRow(item),
      persistedIndex
    }));
    setVatRows(buildInitialRows(existing));
    setRawValues({});
    setGeneralError(null);
    setRemoveConfirmIdx(null);
    setCurrencyCode(currentData.vatSummaryCurrencyCode ?? null);
    setExchangeRate(currentData.vatSummaryExchangeRate?.rate ?? null);
    setRawExchangeRate(null);
    setLastEditedByRow(new Map());
    // Drop the previous session's line-derived amounts; the fetch effect refills.
    setComputed(null);
  }, [open, currentData, vatRateOptions]);

  // -- Derived --
  const calcNetTotal = vatRows.reduce<Decimal>((s, r) => s.plus(r.netAmount), ZERO);
  const calcVatTotal = vatRows.reduce<Decimal>((s, r) => s.plus(r.vatAmount), ZERO);
  const calcGrandTotal = calcNetTotal.plus(calcVatTotal);

  // -- Raw cell helpers --
  const cellKey = (idx: number, f: RowField) => `${idx}-${f}`;
  const cellVal = (idx: number, f: RowField, stored: Decimal | number) => {
    const k = cellKey(idx, f);
    if (k in rawValues) return rawValues[k];
    return typeof stored === 'number' ? String(stored) : stored.toString();
  };

  // Live recompute: 100 ms after the last keystroke in any amount cell, parse
  // pending raw values into `vatRows` so the expected-value hints update while
  // the user types. Raw values stay in `rawValues` (cleared on blur) so the
  // input keeps showing exactly what was typed. `vatRate` is never auto-
  // committed — its blur handler enforces range/duplicate checks that must
  // see the user's final input, not partial keystrokes.
  const debouncedCommit = useMemo(
    () =>
      debounce((raw: Record<string, string>) => {
        setVatRows(prev => {
          let changed = false;
          const next = prev.map((row, i) => {
            const netKey = `${i}-netAmount`;
            const vatKey = `${i}-vatAmount`;
            let updated = row;
            if (netKey in raw) {
              const v = parseAmount(raw[netKey]);
              if (!v.eq(row.netAmount)) {
                updated = { ...updated, netAmount: v };
                changed = true;
              }
            }
            if (vatKey in raw) {
              const v = parseAmount(raw[vatKey]);
              if (!v.eq(row.vatAmount)) {
                updated = { ...updated, vatAmount: v };
                changed = true;
              }
            }
            return updated;
          });
          return changed ? next : prev;
        });
      }, 100),
    []
  );

  useEffect(() => () => debouncedCommit.cancel(), [debouncedCommit]);

  const onCellChange = (idx: number, f: RowField, raw: string) => {
    setRawValues(prev => {
      const next = { ...prev, [cellKey(idx, f)]: raw };
      if (f === 'netAmount' || f === 'vatAmount') debouncedCommit(next);
      return next;
    });
    if (f === 'netAmount' || f === 'vatAmount') {
      setLastEditedByRow(prev => {
        if (prev.get(idx) === f) return prev;
        const next = new Map(prev);
        next.set(idx, f);
        return next;
      });
    }
  };
  const onCellBlur = (idx: number, f: RowField) => {
    const k = cellKey(idx, f);
    const raw = rawValues[k];
    if (raw === undefined) return;

    if (f === 'vatRate') {
      const parsed = parseFloat((raw ?? '').replace(/\s/g, '').replace(',', '.'));
      if (isNaN(parsed) || parsed < 0 || parsed > 100) {
        setRawValues(prev => {
          const n = { ...prev };
          delete n[k];
          return n;
        });
        return;
      }
      const hasDuplicate = vatRows.some((r, i) => i !== idx && r.vatRate === parsed);
      if (hasDuplicate) {
        setRawValues(prev => {
          const n = { ...prev };
          delete n[k];
          return n;
        });
        return;
      }
      setVatRows(prev => {
        const next = [...prev];
        const updated = { ...next[idx], vatRate: parsed };
        // Rate flipped to 0 % → zero the row's VAT to honour the invariant
        // (the VAT cell renders disabled and shows 0 for these rows).
        if (parsed === 0) updated.vatAmount = ZERO;
        next[idx] = updated;
        return next;
      });
      if (parsed === 0) {
        setRawValues(prev => {
          const n = { ...prev };
          delete n[cellKey(idx, 'vatAmount')];
          return n;
        });
      }
    } else {
      setVatRows(prev => {
        const next = [...prev];
        next[idx] = { ...next[idx], [f]: parseAmount(raw) };
        return next;
      });
    }
    setRawValues(prev => {
      const n = { ...prev };
      delete n[k];
      return n;
    });
  };

  /** Apply a programmatic amount to an amount cell — used by the
   * "apply expected value" affordance in the per-cell hint. Cancels any
   * pending debounced commit so a stale typed value doesn't immediately
   * overwrite the applied one, and clears the cell's raw buffer so the
   * displayed value comes from `vatRows`. */
  const setCellAmount = (idx: number, f: 'netAmount' | 'vatAmount', value: Decimal) => {
    debouncedCommit.cancel();
    setVatRows(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [f]: value };
      return next;
    });
    setRawValues(prev => {
      const k = cellKey(idx, f);
      if (!(k in prev)) return prev;
      const n = { ...prev };
      delete n[k];
      return n;
    });
  };

  // The rate keeps its full typed precision — unlike the amounts, it is never
  // rounded to the currency precision. Prefers the raw buffer so an uncommitted
  // keystroke still counts as "the rate currently on screen".
  const effectiveExchangeRate =
    rawExchangeRate !== null ? parseOptionalAmount(rawExchangeRate) : exchangeRate;

  const commitExchangeRate = () => {
    if (rawExchangeRate === null) return;
    setExchangeRate(parseOptionalAmount(rawExchangeRate));
    setRawExchangeRate(null);
  };

  // -- Row actions --
  const removeRow = (idx: number) => {
    const flushed = applyRawValues(vatRows, rawValues);
    setVatRows(flushed.filter((_, i) => i !== idx));
    setRawValues({});
    setLastEditedByRow(new Map());
    setRemoveConfirmIdx(null);
  };

  /** Append a new editable row with an unused starting rate. The row is flagged
   * `isUserAdded` so the rate cell renders as an editable input. */
  const addCustomRow = () => {
    const flushed = applyRawValues(vatRows, rawValues);
    const rate = pickUnusedRate(flushed);
    const next = sortByRate([
      ...flushed,
      { vatRate: rate, netAmount: ZERO, vatAmount: ZERO, isUserAdded: true }
    ]);
    setVatRows(next);
    setRawValues({});
    setLastEditedByRow(new Map());
  };

  // Any line-derived rate missing as a row, or any row whose amounts disagree
  // with the line-derived values — so the toolbar action can disable itself
  // when there is nothing to fill.
  const canFillFromLines = useMemo(() => {
    if (expectedByRate.size === 0) return false;
    for (const [rate, expected] of expectedByRate) {
      const row = vatRows.find(r => r.vatRate === rate);
      if (!row) return true;
      if (differsBeyondTolerance(row.netAmount, expected.netAmount)) return true;
      if (differsBeyondTolerance(row.vatAmount, expected.vatAmount)) return true;
    }
    return false;
  }, [expectedByRate, vatRows]);

  // True when filling would change an existing row that already carries a
  // non-zero amount — i.e. it would overwrite user-entered data, not just fill
  // a blank row or append a new rate. Drives the confirm modal.
  const fillFromLinesWouldOverwrite = useMemo(() => {
    for (const [rate, expected] of expectedByRate) {
      const row = vatRows.find(r => r.vatRate === rate);
      if (!row || (row.netAmount.eq(ZERO) && row.vatAmount.eq(ZERO))) continue;
      if (differsBeyondTolerance(row.netAmount, expected.netAmount)) return true;
      if (differsBeyondTolerance(row.vatAmount, expected.vatAmount)) return true;
    }
    return false;
  }, [expectedByRate, vatRows]);

  /** Fill/correct every row from the line-derived summary, adding rows for
   * rates present in the lines but missing from the table. Rates outside the
   * fixed `vatRateOptions` are flagged `isUserAdded` so their cell stays
   * editable.
   *
   * Recomputes against the live recap currency and the rate as currently typed
   * (including a value not yet committed on blur) rather than replaying the
   * amounts fetched when the editor opened. Falls back to those on failure. */
  const fillFromLines = async () => {
    // Flush pending cell edits and the typed rate before the fetch, so nothing
    // is left buffered while the request is in flight and the rate sent is the
    // one on screen.
    debouncedCommit.cancel();
    const typedRate = effectiveExchangeRate;
    setVatRows(prev => applyRawValues(prev, rawValues));
    setRawValues({});
    setExchangeRate(typedRate);
    setRawExchangeRate(null);

    let fresh: Map<number, ExpectedFromLines> | null = null;
    if (recompute) {
      setFillingFromLines(true);
      try {
        const result = await recompute({
          vatSummaryCurrencyCode: currencyCode,
          exchangeRate: typedRate
        });
        if (result) {
          // Adopt the fresh amounts as the hint baseline too, so the expected
          // values shown next to each cell match what was just filled in.
          setComputed(result);
          fresh = toExpectedByRate(result);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to recompute VAT summary from lines:', e);
      } finally {
        setFillingFromLines(false);
      }
    }

    const source = fresh ?? expectedByRate;
    if (source.size === 0) return;
    const cc = (currencyCode ?? undefined) as CurrencyCode | undefined;
    setVatRows(prev => {
      const next = [...prev];
      for (const [rate, expected] of source) {
        const idx = next.findIndex(r => r.vatRate === rate);
        const row = {
          vatRate: rate,
          netAmount: roundToCurrencyPrecision(expected.netAmount, cc),
          vatAmount: roundToCurrencyPrecision(expected.vatAmount, cc)
        };
        if (idx >= 0) {
          next[idx] = normaliseZeroRateVat({ ...next[idx], ...row });
        } else {
          next.push(
            normaliseZeroRateVat({
              ...row,
              isUserAdded: !vatRateOptions?.some(o => o.value === rate)
            })
          );
        }
      }
      return sortByRate(next);
    });
    setLastEditedByRow(new Map());
  };

  const handleSave = async () => {
    const finalRows = applyRawValues(vatRows, rawValues);
    setVatRows(finalRows);
    setRawValues({});

    setSaving(true);
    try {
      await onSave({
        totalAmountNoVat: currentData.totalAmountNoVat,
        totalAmount: currentData.totalAmount,
        vatSummary: finalRows.map(({ vatRate, netAmount, vatAmount }) => ({
          vatRate: new Decimal(vatRate),
          netAmount,
          vatAmount
        })),
        vatSummaryCurrencyCode: currencyCode,
        vatSummaryExchangeRate: resolveExchangeRateValue(
          effectiveExchangeRate,
          currentData.vatSummaryExchangeRate
        )
      });
      onClose();
    } catch (err) {
      setGeneralError(err instanceof Error ? err.message : errorMessage);
      if (rethrowOnError) throw err;
    } finally {
      setSaving(false);
    }
  };

  const rateLabel = (rate: number) => {
    if (vatRateOptions) {
      const opt = vatRateOptions.find(o => o.value === rate);
      if (opt) return opt.label;
    }
    return `${rate} %`;
  };

  return {
    vatRows,
    saving,
    generalError,
    removeConfirmIdx,
    setRemoveConfirmIdx,
    hasFixedRates,
    calcNetTotal,
    calcVatTotal,
    calcGrandTotal,
    cellVal,
    onCellChange,
    onCellBlur,
    setCellAmount,
    removeRow,
    addCustomRow,
    handleSave,
    rateLabel,
    currencyCode,
    setCurrencyCode,
    /** Display string for the VAT-summary exchange-rate input. */
    exchangeRateDisplay: rawExchangeRate ?? (exchangeRate === null ? '' : exchangeRate.toString()),
    setRawExchangeRate: (value: string) => setRawExchangeRate(value),
    commitExchangeRate,
    lastEditedByRow,
    /** Whether a backend recompute is wired (gates the "fill from lines" button). */
    recomputeEnabled: !!recompute,
    getExpectedForRate,
    canFillFromLines,
    fillFromLinesWouldOverwrite,
    fillFromLines,
    /** True while "Fill from line items" awaits its recompute. */
    fillingFromLines
  };
}

export type UseVatRowEditorResult = ReturnType<typeof useVatRowEditor>;
